const DB_NAME = "cir-storage";
const DB_VERSION = 1;
const STORE_NAME = "app-cache";
const GROUP_DATA_KEY = "group-data";
const LEGACY_LOCAL_STORAGE_GROUP_KEY = "json";

let cache = null;
let hasHydrated = false;
let hydrationPromise = null;

const isBrowser =
  typeof window !== "undefined" && typeof window.indexedDB !== "undefined";

const openDatabase = () => {
  if (!isBrowser) {
    return Promise.resolve(null);
  }

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const withStore = async (mode, handler) => {
  const db = await openDatabase();

  if (!db) {
    return null;
  }

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);

    let operationResult = null;

    transaction.oncomplete = () => {
      resolve(operationResult);
      db.close();
    };
    transaction.onerror = () => {
      reject(transaction.error);
      db.close();
    };
    transaction.onabort = () => {
      reject(transaction.error);
      db.close();
    };

    const request = handler(store);

    if (!request) {
      operationResult = null;
      return;
    }

    request.onsuccess = () => {
      operationResult = request.result;
    };
    request.onerror = () => {
      reject(request.error);
      db.close();
    };
  });
};

const readIndexedDbGroupData = async () => {
  return withStore("readonly", (store) => store.get(GROUP_DATA_KEY));
};

const writeIndexedDbGroupData = async (groupData) => {
  return withStore("readwrite", (store) =>
    store.put(groupData, GROUP_DATA_KEY),
  );
};

const clearIndexedDbGroupData = async () => {
  return withStore("readwrite", (store) => store.delete(GROUP_DATA_KEY));
};

const parseLegacyLocalStorage = () => {
  if (typeof localStorage === "undefined") {
    return null;
  }

  const legacyValue = localStorage.getItem(LEGACY_LOCAL_STORAGE_GROUP_KEY);
  if (!legacyValue) {
    return null;
  }

  try {
    return JSON.parse(legacyValue);
  } catch (_error) {
    return null;
  }
};

const removeLegacyLocalStorageGroupData = () => {
  if (typeof localStorage === "undefined") {
    return;
  }

  localStorage.removeItem(LEGACY_LOCAL_STORAGE_GROUP_KEY);
};

const setHydratedCache = (groupData) => {
  cache = groupData ?? null;
  hasHydrated = true;
};

export const hydrateGroupDataCache = async () => {
  if (hasHydrated) {
    return cache;
  }

  if (hydrationPromise) {
    return hydrationPromise;
  }

  hydrationPromise = (async () => {
    try {
      const indexedDbData = await readIndexedDbGroupData();

      if (indexedDbData !== undefined) {
        setHydratedCache(indexedDbData);
        removeLegacyLocalStorageGroupData();
        return cache;
      }

      const legacyData = parseLegacyLocalStorage();
      setHydratedCache(legacyData);

      if (legacyData !== null) {
        await writeIndexedDbGroupData(legacyData);
      }

      removeLegacyLocalStorageGroupData();
      return cache;
    } catch (error) {
      console.error("Failed to hydrate groupData from IndexedDB", error);

      const legacyData = parseLegacyLocalStorage();
      setHydratedCache(legacyData);
      removeLegacyLocalStorageGroupData();

      return cache;
    }
  })();

  return hydrationPromise;
};

export const waitForGroupDataHydration = async () => {
  return hydrateGroupDataCache();
};

export const getGroupDataSync = () => {
  return cache;
};

export const setGroupDataSync = (groupData) => {
  cache = groupData ?? null;

  const persistPromise =
    cache === null ? clearIndexedDbGroupData() : writeIndexedDbGroupData(cache);

  removeLegacyLocalStorageGroupData();

  return persistPromise;
};

export const resetGroupDataSync = () => {
  cache = null;

  clearIndexedDbGroupData().catch((error) => {
    console.error("Failed to clear groupData from IndexedDB", error);
  });

  removeLegacyLocalStorageGroupData();
};
